import { useEffect, useMemo, useState } from "react";
import Image from "next/image";
import { useRouter } from "next/router";
import styles from "./Footer.module.css";
import { FaFacebookF, FaInstagram, FaLinkedinIn, FaPhoneAlt, FaPinterest, FaTiktok, FaYoutube } from "react-icons/fa";
import { FaLocationDot, FaXTwitter } from "react-icons/fa6";
import { IoLogoWhatsapp } from "react-icons/io";
import { map, orderBy } from "lodash";
import { useAuth } from "@/Common/Auth/AuthContext";
import { withRegionPath } from "@/utils/session";
import { filterNavItemsForIndia } from "@/utils/region";
import { fetchFooterHeading } from "@/services/api/common";
import { normalizeFooterHeadingsResponse } from "@/utils/footerHelpers";
import { isIndianUser } from "@/utils/getCountryCode";

const DISCOVER = [
  { label: "About Us", href: "/about-us" },
  { label: "Blogs", href: "/blog" },
  { label: "Deals", href: "/deals" },
  { label: "Coupon", href: "/coupons" },
];

const SERVICES = [
  { label: "Holidays", href: "/holiday" },
  { label: "Stays", href: "/stays" },
  { label: "Flights", href: "/flights" },
  { label: "Activities", href: "/activity" },
];

const GUEST_SERVICES = [
  { label: "Contact Us", href: "/contact-us" },
  { label: "Testimonials", href: "/testimonials" },
  { label: "Login/Signup", href: "/login" },
  // { label: "Register", href: "/signup" },
  // { label: "Travel Guides", href: "/travel-guides" },
];
const AWARDS = [
  { label: "Award1", img: "/assets/images/Award1.png" },
  { label: "Award2", img: "/assets/images/Award2.png" },
];

const OFFICES = [
  {
    city: "Dubai",
    address: "1210-1211, Regal Tower, Business Bay Dubai, UAE",
  },
  {
    city: "India",
    address:
      "Sanganer Besides JVJ, Plot No. 12-A, A Gram, Ramnagariya, Jagatpura, Jaipur, Rajasthan 302017",
  },
];

const SOCIALS = isIndianUser() ?
  [
    { label: "YouTube", href: "https://www.youtube.com/@nextholidays_India", icon: <FaYoutube /> },
    { label: "X", href: "https://x.com/nextholidays_in", icon: <FaXTwitter /> },
    { label: "Pinterest", href: "https://in.pinterest.com/nextholidaysindia/", icon: <FaPinterest /> },
    { label: "Instagram", href: "https://www.instagram.com/nextholidaysindia/", icon: <FaInstagram /> },
    { label: "Facebook", href: "https://www.facebook.com/nextholidaysindia/", icon: <FaFacebookF /> },
    { label: "LinkedIn", href: "https://www.linkedin.com/company/next-holidays-india/", icon: <FaLinkedinIn /> },
    { label: "WhatsApp", href: "https://whatsapp.com/channel/0029Vb69u7bJ3juvuv0BK70V/", icon: <IoLogoWhatsapp /> },
  ]
  :
  [
    { label: "YouTube", href: "https://www.youtube.com/@nextholidaysuae", icon: <FaYoutube /> },
    { label: "X", href: "https://x.com/NextHolidaysuae", icon: <FaXTwitter /> },
    { label: "TikTok", href: "https://www.tiktok.com/@nextholidaysuae", icon: <FaTiktok /> },
    { label: "Pinterest", href: "https://pinterest.com/nextholidayscom", icon: <FaPinterest /> },
    { label: "Instagram", href: "https://www.instagram.com/nextholidaysuae/", icon: <FaInstagram /> },
    { label: "Facebook", href: "https://facebook.com/nextholidayscom/", icon: <FaFacebookF /> },
    { label: "LinkedIn", href: "https://www.linkedin.com/company/nextholidaysuae/", icon: <FaLinkedinIn /> },
    { label: "WhatsApp", href: "https://whatsapp.com/channel/0029Vb69u7bJ3juvuv0BK70V/", icon: <IoLogoWhatsapp /> },
  ];

function Chevron({ open }) {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
      className={open ? styles.chevronOpen : undefined}
    >
      <path
        d="M6 9l6 6 6-6"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

const AUTH_FOOTER_PATHS = ["/login", "/signup", "/register"];

function getFooterHref(href = "", loggedIn = false) {
  if (!href) return "#";
  if (/^https?:\/\//i.test(href)) return href;
  if (loggedIn && AUTH_FOOTER_PATHS.includes(href)) {
    return withRegionPath("/");
  }
  if (AUTH_FOOTER_PATHS.includes(href)) {
    return withRegionPath(href);
  }
  return withRegionPath(href);
}

function LinkColumn({ title, links, loggedIn = false }) {
  return (
    <div className={styles.col}>
      <h3 className={styles.colTitle}>{title}</h3>
      <ul className={styles.linkList}>
        {links.map((item) => (
          <li key={item.href + item.label}>
            <a href={getFooterHref(item.href, loggedIn)}>{item.label}</a>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function Footer() {
  const router = useRouter();
  const { loggedIn } = useAuth();
  const [sections, setSections] = useState([]);
  const [openId, setOpenId] = useState(null);
  const isIndia = router.asPath.startsWith("/in");

  const visibleServices = useMemo(
    () => filterNavItemsForIndia(SERVICES, isIndia),
    [isIndia]
  );

  const visibleSections = useMemo(
    () =>
      sections
        .map((section) => ({
          ...section,
          links: filterNavItemsForIndia(section.links, isIndia),
        }))
        .filter((section) => section.links.length > 0),
    [sections, isIndia]
  );

  useEffect(() => {
    let isMounted = true;

    const loadFooterHeadings = async () => {
      try {
        const response = await fetchFooterHeading();
        const normalized = normalizeFooterHeadingsResponse(response);

        if (!isMounted) return;

        setSections(normalized);
        // setOpenId(normalized[0]?.id ?? null);
      } catch (error) {
        console.error("Footer headings fetch error:", error);
        if (isMounted) {
          setSections([]);
          setOpenId(null);
        }
      }
    };

    loadFooterHeadings();

    return () => {
      isMounted = false;
    };
  }, []);

  const toggle = (id) => {
    setOpenId((prev) => (prev === id ? null : id));
  };

  return (
    <footer className={styles.footer}>
      {visibleSections.length > 0 ? (
        <div className={`standard-padding ${styles.accordionWrap}`}>
          {visibleSections.map((section) => {
            const open = openId === section.id;
            return (
              <div key={section.id} className={styles.accordionItem}>
                <button
                  type="button"
                  className={styles.accordionBtn}
                  aria-expanded={open}
                  aria-controls={`footer-acc-${section.id}`}
                  onClick={() => toggle(section.id)}
                >
                  <span>{section.title}</span>
                  <Chevron open={open} />
                </button>
                <div
                  id={`footer-acc-${section.id}`}
                  className={`${styles.accordionPanel} ${open ? styles.accordionPanelOpen : ""}`}
                  hidden={!open}
                >
                  <div className={styles.accordionLinks}>
                    {orderBy(section.links, "label", "asc").map((link, index) => (
                      <span key={`${section.id}-${link.href}-${index}`} className={styles.accordionLinkWrap}>
                        {index > 0 ? <span className={styles.pipe} aria-hidden="true">|</span> : null}
                        <a href={getFooterHref(link.href, loggedIn)}>{link.label}</a>
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : null}

      <div className={styles.main}>
        <div className={styles.mainBg} aria-hidden="true">
          <Image
            src="/assets/images/footer.png"
            alt=""
            fill
            className={styles.mainBgImg}
            sizes="100vw"
          />
          <div className={styles.mainOverlay} />
        </div>

        <div className={`standard-padding ${styles.mainInner}`}>
          <div className={styles.grid}>
            <div className={styles.brandCol}>
              <a href="/" className={styles.brand} aria-label="Next Holidays home">
                <Image
                  src={isIndia ? "/images/IndiaLogoW.svg" : "/assets/images/NHLogoW.png"}
                  alt="Next Holidays"
                  width={180}
                  height={44}
                  className={styles.logo}
                />
              </a>
              {/* <p className={styles.tagline}>Crafted by Experts, Made for You</p> */}
              <p className={styles.desc}>
                Creating unforgettable travel experiences since 2010. Your trusted
                partner for worldwide adventures.
              </p>
              <div className={styles.awardsWrap}>
                {map(AWARDS, (award) => (
                  <Image
                    key={award.label}
                    src={award.img}
                    alt={award.label}
                    height={40}
                    width={90}
                  />
                ))}
              </div>
            </div>

            <LinkColumn title="Discover Us" links={DISCOVER} loggedIn={loggedIn} />
            <LinkColumn title="Services" links={visibleServices} loggedIn={loggedIn} />
            <LinkColumn title="Guest Services Links" links={GUEST_SERVICES} loggedIn={loggedIn} />

            <div className={`${styles.col} ${styles.contactCol}`}>
              <h3 className={`${styles.colTitle} ${styles.followUsTitle}`}>Follow Us</h3>
              {/* 
              <div className={styles.officesBlock}>
                {OFFICES.map((office) => (
                  <div key={office.city} className={styles.addressBlock}>
                    <FaLocationDot className={styles.pin} aria-hidden="true" />
                    <div>
                      <p className={styles.officeCity}>{office.city}</p>
                      <p className={styles.officeAddress}>{office.address}</p>
                    </div>
                  </div>
                ))}
              </div> */}

              <div className={styles.socials}>
                {SOCIALS.map((item) => (
                  <a
                    key={item.label}
                    href={item.href}
                    className={styles.socialBtn}
                    aria-label={item.label}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {item.icon}
                  </a>
                ))}
              </div>
              <p className={styles.tollLabel}>{isIndianUser() ? "24x7 Support" : "Toll Free"}</p>
              {isIndianUser() ?
                <a href="tel:+919610006398" className={styles.callBtn}>
                  <span className={styles.callIcon}>
                    <FaPhoneAlt size={16} />
                  </span>
                  <span>+91 96100 06398</span>
                </a>
                :
                <a href="tel:+971800500777" className={styles.callBtn}>
                  <span className={styles.callIcon}>
                    <FaPhoneAlt size={16} />
                  </span>
                  <span>+971 800 500777</span>
                </a>
              }
            </div>
          </div>

          <div className={styles.legal}>
            <hr className={styles.legalDivider} />
            <div className={styles.legalLinks}>
              <a href={withRegionPath("/privacy-policy")}>Privacy Policy</a>
              <span aria-hidden="true">|</span>
              <a href={withRegionPath("/termsandcondition")}>
                Terms &amp; Conditions
              </a>
            </div>
            <hr className={styles.legalDivider} />
            <p className={styles.copy}>
              Copyright 2026 © Next Holidays. All Rights Reserved.
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}